import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopMenu from "../../components/TopMenu/TopMenu";
import BottomNav from "../../components/BottomNav/BottomNav";
import getPlaces, { Place } from "../../api/PlacesApi";
import Table, { TableColumn } from "../../components/Table/Table";
import "./Cadastros.css";

type PlaceRow = {
  id: string;
  nome: string;
};

function getPlaceName(place: Place): string {
  const rawName = (place as any).name;
  return typeof rawName === "string" && rawName.trim().length > 0
    ? rawName
    : String((place as any).code ?? place.id);
}

function getPlaceUrlCode(place: Place): string {
  const code = (place as any).code;
  if (typeof code === "string") {
    return code.trim();
  }

  if (typeof code === "number") {
    return String(code);
  }

  return "";
}

export default function CadastrosLocal() {
  const navigate = useNavigate();
  const [places, setPlaces] = useState<Place[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoadingPlaces(true);
    setError(null);

    getPlaces()
      .then((response) => {
        setPlaces(response);
      })
      .catch((err) => {
        console.error("Erro ao carregar locais para cadastros:", err);
        setError("Nao foi possivel carregar os locais.");
      })
      .finally(() => {
        setLoadingPlaces(false);
      });
  }, []);

  const placeRows = useMemo<PlaceRow[]>(() => {
    return places
      .map((place) => ({
        id: getPlaceUrlCode(place),
        nome: getPlaceName(place)
      }))
      .filter((place) => place.id.length > 0);
  }, [places]);

  const filteredPlaceRows = useMemo<PlaceRow[]>(() => {
    const normalize = (value: string) =>
      value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    if (searchTerm.trim().length === 0) {
      return placeRows;
    }

    return placeRows.filter((place) => normalize(place.nome).includes(normalize(searchTerm)));
  }, [placeRows, searchTerm]);

  const columns: TableColumn<PlaceRow>[] = useMemo(
    () => [
      {
        id: "nome",
        header: "LOCAL",
        render: (place) => place.nome
      }
    ],
    []
  );

  function buildPresencialHref(placeId: string): string {
    const encodedId = encodeURIComponent(placeId);
    return `/plantoes-cadastrados?tipo=presencial&localId=${encodedId}`;
  }

  return (
    <div className="cadastros-page">
      <TopMenu />

      <main className="cadastros-content">
        <section className="cadastros-hero">
          <p className="cadastros-hero-title">Cadastros</p>
          <p className="cadastros-hero-subtitle">Escolha o local para cadastro presencial</p>
        </section>

        <section className="cadastros-places-section">
          <div className="cadastros-controls">
            <button
              type="button"
              className="cadastros-back-button"
              onClick={() => navigate("/cadastros")}
            >
              Voltar
            </button>

            <div className="cadastros-search-input-wrapper">
              <input
                type="text"
                className="cadastros-search-input"
                placeholder="Buscar local..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="cadastros-search-clear"
                  aria-label="Limpar busca"
                  onClick={() => setSearchTerm("")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {loadingPlaces && <p className="cadastros-info">Carregando locais...</p>}
          {error && <p className="cadastros-error">{error}</p>}

          {!loadingPlaces && !error && filteredPlaceRows.length > 0 && (
            <Table<PlaceRow>
              bodyRowClassName="cadastros-row"
              columns={columns}
              data={filteredPlaceRows}
              getRowKey={(place) => place.id}
              gridTemplateColumns="1fr"
              headerRowClassName="cadastros-row cadastros-header"
              linkRowClassName="cadastros-link"
              rowHref={(place) => buildPresencialHref(place.id)}
              wrapperClassName="cadastros-table-wrapper"
            />
          )}

          {!loadingPlaces && !error && filteredPlaceRows.length === 0 && (
            <p className="cadastros-info">Nenhum local encontrado para o filtro atual.</p>
          )}
        </section>
      </main>

      <BottomNav />
    </div>
  );
}
